// Aggregated reactions row, rendered directly below a `MessageBubble`.
// Surfaces the per-emoji count produced by collapsing every
// `0x30 REACTION` envelope (§2.4) that targets the same `event_id`.
//
// Figma reference: `2804:27483 Message Interaction → reactions strip`.
// Layout:
//   • Pill per emoji with the emoji glyph + integer count.
//   • Pill is `accent.indigo` tinted at 12% when the viewer is one of
//     the reactors (helps people see at a glance which reactions are
//     theirs).
//   • Tap a pill to toggle the viewer's reaction (host owns the wire
//     send through `onToggleReaction`).
//   • Trailing "+" chip opens the host's reaction-picker overlay.
//
// Pure presentational — every aggregation is done upstream. The
// chat surface keeps a `reactionsByMessageId: Map<MessageId,
// ReactionAggregate[]>` sidecar that the host populates from its
// reaction projection (the same projection drives notification
// badge counts).

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, StyleSheet, Text, type TextStyle, View } from 'react-native';
import { useColorScheme } from '../foundations/colorScheme';
import { tokens } from '../design-system/tokens';
import { ReactionReactorsPopover } from './ReactionReactors';
import { WebPortal } from './composer/WebPortal';

const IS_WEB = typeof document !== 'undefined';

export interface ReactionAggregate {
  /** Unicode emoji codepoints (matches `ReactionBody.emoji` in §2.4). */
  readonly emoji: string;
  /** Number of distinct reactors. */
  readonly count: number;
  /** True when the viewer is one of the reactors. */
  readonly reactedByMe: boolean;
  /**
   * Account ids of every reactor for this emoji, earliest first. Powers
   * the hover popover that lists who reacted (Figma 3992:74151). Optional
   * so hosts that haven't wired the projection still render counts.
   */
  readonly reactorAccountIds?: ReadonlyArray<string> | undefined;
}

export interface ReactionsRowProps {
  readonly reactions: ReadonlyArray<ReactionAggregate>;
  /**
   * Anchor side — same `isSelf` discriminator the bubble uses so the
   * row hugs the same edge of the column.
   */
  readonly isSelf: boolean;
  /** Toggle reaction on/off for the viewer. */
  readonly onToggleReaction?: ((emoji: string) => void) | undefined;
  /** Open the picker overlay. */
  readonly onOpenPicker?: (() => void) | undefined;
  readonly testID?: string | undefined;
}

const MAX_COUNT_DISPLAY = 99;

export const ReactionsRow = React.memo<ReactionsRowProps>(
  ({ reactions, isSelf, onToggleReaction, onOpenPicker, testID }) => {
    const scheme = useColorScheme();
    const colorScheme: 'light' | 'dark' =
      scheme === 'dark' ? 'dark' : 'light';
    const colors = tokens.color.semantic[colorScheme];

    // De-dupe identical emoji entries on the way in (defensive — the
    // host should already have collapsed them, but a stray
    // re-aggregation in dev causes noisy duplicates).
    const collapsed = useMemo(() => {
      const map = new Map<string, ReactionAggregate>();
      for (const r of reactions) {
        if (r.count <= 0) continue;
        const existing = map.get(r.emoji);
        if (existing) {
          map.set(r.emoji, {
            emoji: r.emoji,
            count: existing.count + r.count,
            reactedByMe: existing.reactedByMe || r.reactedByMe,
            reactorAccountIds: [
              ...(existing.reactorAccountIds ?? []),
              ...(r.reactorAccountIds ?? []),
            ],
          });
        } else {
          map.set(r.emoji, r);
        }
      }
      return Array.from(map.values());
    }, [reactions]);

    // Per-pill element refs — used on web to measure viewport rect so the
    // portaled reactor popover can be positioned with `position: fixed`.
    const pillRefs = useRef<Map<string, unknown>>(new Map());
    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Reactor popover state: tracks which pill is hovered + its rect + its reactors.
    const [hoveredState, setHoveredState] = useState<{
      emoji: string;
      rect: { top: number; left: number; right: number };
      reactorIds: readonly string[];
    } | null>(null);
    // Native (react-native-macos) reactor popover — positioned with RN
    // absolute layout inside the pill's `position:relative` wrapper (no DOM
    // rect measuring / portal). Kept separate from the web `hoveredState`.
    const [nativeHovered, setNativeHovered] = useState<{
      emoji: string;
      reactorIds: readonly string[];
    } | null>(null);

    const cancelClose = (): void => {
      if (closeTimerRef.current !== null) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
    const scheduleClose = (): void => {
      cancelClose();
      closeTimerRef.current = setTimeout(() => {
        setHoveredState(null);
        setNativeHovered(null);
        closeTimerRef.current = null;
      }, 120);
    };

    useEffect(() => () => { cancelClose(); }, []);

    if (collapsed.length === 0 && onOpenPicker === undefined) return null;

    const rowStyle = [styles.row, isSelf ? styles.rowSelf : styles.rowOther];

    // Reactor popover portaled to <body> at fixed coordinates so it paints
    // above every stacking context — including the message-actions panel.
    const portal =
      IS_WEB && hoveredState !== null
        ? React.createElement(
            WebPortal,
            null,
            React.createElement('div', {
              style: {
                position: 'fixed',
                top: hoveredState.rect.top - 6,
                transform: 'translateY(-100%)',
                ...(isSelf
                  ? {
                      right:
                        (typeof window !== 'undefined' ? window.innerWidth : 0) -
                        hoveredState.rect.right,
                    }
                  : { left: hoveredState.rect.left }),
                zIndex: 200000,
                pointerEvents: 'auto',
              },
              onMouseEnter: cancelClose,
              onMouseLeave: scheduleClose,
            },
            React.createElement(ReactionReactorsPopover, {
              emoji: hoveredState.emoji,
              accountIds: hoveredState.reactorIds,
            }),
            ),
          )
        : null;

    return (
      <>
        <View style={rowStyle} testID={testID} accessibilityLabel="Reactions">
          {collapsed.map((r) => {
            const bg = r.reactedByMe ? '#537EFF1F' : colors.surface.tertiary;
            const border = r.reactedByMe ? '#537EFF' : '#222429';
            const fg: TextStyle = {
              color: r.reactedByMe ? colors.accent.indigo : colors.text.primary,
            };
            const countText =
              r.count > MAX_COUNT_DISPLAY ? `${MAX_COUNT_DISPLAY}+` : `${r.count}`;
            return (
              <View
                key={r.emoji}
                style={styles.pillWrap}
                ref={(el) => {
                  if (el !== null) pillRefs.current.set(r.emoji, el);
                  else pillRefs.current.delete(r.emoji);
                }}
              >
                <Pressable
                  onPress={() => onToggleReaction?.(r.emoji)}
                  onHoverIn={() => {
                    const reactorIds = r.reactorAccountIds ?? [];
                    if (reactorIds.length === 0) return;
                    cancelClose();
                    if (IS_WEB) {
                      const el = pillRefs.current.get(r.emoji);
                      if (el !== undefined) {
                        const rect = (el as HTMLElement).getBoundingClientRect();
                        setHoveredState({
                          emoji: r.emoji,
                          rect: { top: rect.top, left: rect.left, right: rect.right },
                          reactorIds,
                        });
                      }
                    } else {
                      setNativeHovered({ emoji: r.emoji, reactorIds });
                    }
                  }}
                  onHoverOut={() => {
                    scheduleClose();
                  }}
                  accessibilityRole="button"
                  accessibilityLabel={`${r.emoji} ${countText}${r.reactedByMe ? ', your reaction' : ''}`}
                  style={[styles.pill, { backgroundColor: bg, borderColor: border }]}
                >
                  <Text style={styles.emoji}>{r.emoji}</Text>
                  <Text style={[styles.count, fg]}>{countText}</Text>
                </Pressable>
                {/* Native reactor popover — floats above this pill (RN
                    absolute) when hovered on react-native-macos. */}
                {!IS_WEB && nativeHovered?.emoji === r.emoji ? (
                  <View
                    pointerEvents="box-none"
                    style={[
                      styles.nativePopover,
                      isSelf ? styles.nativePopoverSelf : styles.nativePopoverOther,
                    ]}
                    {...({
                      onMouseEnter: cancelClose,
                      onMouseLeave: scheduleClose,
                    } as object)}
                  >
                    <ReactionReactorsPopover
                      emoji={nativeHovered.emoji}
                      accountIds={nativeHovered.reactorIds}
                    />
                  </View>
                ) : null}
              </View>
            );
          })}
          {onOpenPicker !== undefined ? (
            <Pressable
              onPress={onOpenPicker}
              accessibilityRole="button"
              accessibilityLabel="Add reaction"
              style={[
                styles.pill,
                styles.pickerPill,
                {
                  backgroundColor: colors.surface.tertiary,
                  borderColor: colors.border.secondary,
                },
              ]}
            >
              <Text style={[styles.pickerGlyph, { color: colors.text.tertiary }]}>
                +
              </Text>
            </Pressable>
          ) : null}
        </View>
        {portal}
      </>
    );
  },
);

ReactionsRow.displayName = 'ReactionsRow';

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: 4,
    // paddingHorizontal: tokens.spacing.xs,
  },
  rowSelf: {
    justifyContent: 'flex-end',
  },
  rowOther: {
    justifyContent: 'flex-start',
  },
  pillWrap: {
    position: 'relative',
  },
  // Native reactor popover: float just above the pill. Anchored to the
  // matching edge so it hugs the same side the reactions row does.
  nativePopover: {
    position: 'absolute',
    bottom: '100%',
    marginBottom: 6,
    zIndex: 200000,
  },
  nativePopoverSelf: {
    right: 0,
  },
  nativePopoverOther: {
    left: 0,
  },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: tokens.spacing.xs,
    paddingVertical: 2,
    borderRadius: 9999,
    borderWidth: StyleSheet.hairlineWidth,
  },
  pickerPill: {
    paddingHorizontal: tokens.spacing.sm,
  },
  emoji: {
    fontSize: 14,
    lineHeight: 16,
  },
  count: {
    fontSize: 11,
    lineHeight: 17,
    fontWeight: '400',
  },
  pickerGlyph: {
    fontSize: 14,
    lineHeight: 16,
    fontWeight: '600',
  },
});
